import { z } from "zod";
import { CarFormPartModel } from "./carFormPartModel";
import LicensePlate from "license-plate";
import { Form } from "react-bootstrap";
import { LicencePlateField } from "../../../../general/fields/modules/commons/licencePlateField/licencePlateField";
import { useTranslation } from "react-i18next";
import { relationSchema } from "../../../../relation/domain/models/RelationModel";
import { EntitySelectField } from "../../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { RequiredAsterisks } from "../../../../general/modules/commons/Asterisks/requiredAsterisks";
import { customerSchema } from "../../../../customer/domain/model/CustomerModel";
import useCrudFilters from "../../../../../hooks/useCrudFilters";
import { useAuthorization } from "../../../../../hooks/useAuthorization";

export const CarFormPart = ({ form }: CarFormPartModel) => {
  const { t } = useTranslation("common");

  const relationCrudFilter = {
    searchBy: "SearchByRootRelation",
    fields: {},
  };

  const customerCrudFilter = useCrudFilters({
    searchBy: "SearchByRootCustomer",
    fields: {
      relation: {
        fields: {
          id: {
            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
            /* @ts-ignore*/
            equal: form.getFieldValue("relationId"),
          },
        },
      },
    },
  });

  const { hasRole } = useAuthorization();

  // Determine if the licence plate can be edited
  const isNew = !form.getFieldValue("id");
  const licenseDisabled =
    !isNew && !hasRole("super-admin") && !hasRole("dbh-manager");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore*/}
      {form.state.errors ? (
        <div className={"invalid-feedback d-block"}>
          {form.state.errors.map((error) => t(error + "")).join(", ")}
        </div>
      ) : null}

      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore*/}
      <form.Field
        key="licencePlate"
        name="licencePlate"
        validators={{
          onChange: z
            .string()
            .optional()
            .nullable()
            .refine((data) => {
              if (data && data?.replace(/-/g, "").length > 5) {
                const plate = new LicensePlate(data);
                return plate.valid();
              }
              return true;
            }, t("Invalid licence plate")),
          onSubmit: z
            .string()
            .optional()
            .nullable()
            .refine((data) => {
              if (data) {
                const plate = new LicensePlate(data);
                return plate.valid();
              }
              return true;
            }, t("Invalid licence plate")),
        }}
      >
        {(field) => {
          return (
            <Form.Group className="mb-3">
              <Form.Label>{t("Licence plate")}</Form.Label>
              <LicencePlateField
                id={field.name}
                key={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                disabled={licenseDisabled}
                onChange={(e) => {
                  if (e.target.value.replace(/-/g, "").length > 6) return;
                  const plate = new LicensePlate(e.target.value);
                  if (plate.valid()) {
                    field.handleChange(plate.pretty());
                  } else {
                    field.handleChange(e.target.value);
                  }
                }}
                isInvalid={field.state.meta.errors.length > 0}
              />
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore*/}
              {field.state.meta.errors ? (
                <Form.Control.Feedback type="invalid">
                  {field.state.meta.errors.join(", ")}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
          );
        }}
      </form.Field>
      <form.Field
        name="additional_info"
        key="additional_info"
        validators={{
          onChange: z
            .string()
            .max(30)
            .optional()
            .nullable()
            .refine((data) => {
              if (data) {
                return data.length <= 30;
              }
              return true;
            }, t("Invalid additional info")),
          onSubmit: z
            .string()
            .max(30)
            .optional()
            .nullable()
            .refine((data) => {
              if (data) {
                return data.length <= 30;
              }
              return true;
            }, t("Invalid additional info")),
        }}
      >
        {(field) => {
          return (
            <Form.Group className="mb-3">
              <Form.Label>{t("Additional info")}</Form.Label>
              <Form.Control
                as="input"
                id={field.name}
                key={field.name}
                name={field.name}
                value={field.state.value || ""}
                disabled={licenseDisabled}
                placeholder={
                  !field.state.value
                    ? t("Additional info placeholder")
                    : undefined
                }
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore*/}
              {field.state.meta.errors ? (
                <div style={{ color: "red" }}>
                  {String(field.state?.meta?.errors[0])?.split(",")[1]?.trim()}
                </div>
              ) : null}
            </Form.Group>
          );
        }}
      </form.Field>
      <form.Field
        name="vin"
        key="vin"
        validators={{
          onChange: z.string().optional().nullable(),
        }}
      >
        {(field) => {
          return (
            <Form.Group className="mb-3">
              <Form.Label>
                {t("VIN (Vehicle identification number)")}
              </Form.Label>
              <Form.Control
                as="input"
                id={field.name}
                key={field.name}
                name={field.name}
                value={field.state.value || ""}
                placeholder={
                  !field.state.value
                    ? t("VIN (Vehicle identification number)")
                    : undefined
                }
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore*/}
              {field.state.meta.errors ? (
                <Form.Control.Feedback type="invalid">
                  {field.state.meta.errors.join(", ")}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
          );
        }}
      </form.Field>
      <form.Field
        name="relationId"
        key="relation"
        validators={{
          onChange: z.string(),
        }}
      >
        {(field) => {
          return (
            <Form.Group className="mb-3">
              <Form.Label>
                {t("Dealer")} <RequiredAsterisks />
              </Form.Label>
              <EntitySelectField
                entity="Relation"
                prefix="relations"
                id={field.name}
                name={field.name}
                onBlur={field.handleBlur}
                isInvalid={field.state.meta.errors.length > 0}
                value={field.state.value}
                onChange={(option) => {
                  const newValue = (option as { value?: string })?.value;

                  if (newValue != null && newValue == field.state.value) return;

                  if (option === null) {
                    customerCrudFilter.removeFilter("relation.id");
                    return;
                  }
                  customerCrudFilter.updateFilter(
                    "relation.id",
                    (option as { value: string }).value,
                  );
                  field.handleChange((option as { value: string }).value);
                  form.setFieldValue("customerId", undefined);
                }}
                schema={relationSchema}
                limit={100}
                filters={relationCrudFilter}
              />
              {field.state.meta.errors ? (
                <Form.Control.Feedback type="invalid">
                  {t(field.state.meta.errors.join(", "))}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
          );
        }}
      </form.Field>

      <form.Field
        name="customerId"
        key="customer"
        validators={{
          onChange: z.string().optional().nullable(),
          onSubmit: z.string().optional().nullable(),
        }}
      >
        {(field) => {
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          /* @ts-ignore*/
          const relationId = form.getFieldValue("relationId");

          return (
            <Form.Group className="mb-3">
              <Form.Label>{t("Customer")}</Form.Label>
              <EntitySelectField
                entity="Customer"
                prefix="customers"
                id={field.name}
                name={field.name}
                onBlur={field.handleBlur}
                isInvalid={field.state.meta.errors.length > 0}
                value={field.state.value}
                onChange={(option) => {
                  field.handleChange((option as { value?: string })?.value);
                }}
                schema={customerSchema}
                limit={5000}
                filters={customerCrudFilter.filters}
                isDisabled={relationId == null}
              />
              {field.state.meta.errors ? (
                <Form.Control.Feedback type="invalid">
                  {t(field.state.meta.errors.join(", "))}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
          );
        }}
      </form.Field>
    </form>
  );
};
